import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import styles from '../../styles/modules/admin/AdminPackages.module.css';
import adminCommonStyles from '../../styles/modules/admin/AdminCommon.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface PackageGrant {
  packageType: string;
  count?: number;
  quantity?: number;
}

interface AvailablePackage {
  key: string;
  label: string;
  price: number | null;
  currency: string | null;
  isActive: boolean;
  grants: PackageGrant[];
  sortOrder: number;
}

interface EditForm {
  label: string;
  price: string;
  currency: string;
  isActive: boolean;
  sortOrder: string;
}

interface GrantRow {
  packageType: string;
  quantity: string;
}

interface CreateForm {
  key: string;
  label: string;
  price: string;
  currency: string;
  isActive: boolean;
  sortOrder: string;
  grants: GrantRow[];
}

interface EditFormWithGrants extends EditForm {
  grants: GrantRow[];
}

const emptyCreateForm = (): CreateForm => ({
  key: '',
  label: '',
  price: '',
  currency: '',
  isActive: true,
  sortOrder: '',
  grants: [],
});

const AdminPackages: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const confirm = useConfirm();

  const [packages, setPackages] = useState<AvailablePackage[]>([]);
  const [loading, setLoading] = useState(true);

  // edit
  const [editTarget, setEditTarget] = useState<AvailablePackage | null>(null);
  const [editForm, setEditForm] = useState<EditFormWithGrants>({ label: '', price: '', currency: '', isActive: true, sortOrder: '', grants: [] });
  const [saving, setSaving] = useState(false);

  // create
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(emptyCreateForm());
  const [creating, setCreating] = useState(false);

  // delete
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('api/admin/available-packages'), {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const list: AvailablePackage[] = Array.isArray(data)
          ? data
          : data.data ?? data.packages ?? data.items ?? [];
        setPackages(list);
      } else {
        showToast({ message: t('admin.packages.fetchFailed', { defaultValue: 'Failed to load packages.' }), type: 'error' });
      }
    } catch (error) {
      console.error('Error fetching admin packages:', error);
      showToast({ message: t('admin.packages.fetchFailed', { defaultValue: 'Failed to load packages.' }), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (pkg: AvailablePackage) => {
    setEditTarget(pkg);
    setEditForm({
      label: pkg.label,
      price: pkg.price !== null && pkg.price !== undefined ? String(pkg.price) : '',
      currency: pkg.currency ?? '',
      isActive: pkg.isActive,
      sortOrder: String(pkg.sortOrder),
      grants: pkg.grants.map(g => ({ packageType: g.packageType, quantity: String(g.quantity ?? g.count ?? 1) })),
    });
  };

  const closeEdit = () => setEditTarget(null);

  const validatePrice = (raw: string): { valid: boolean; value: number | null } => {
    if (raw.trim() === '') return { valid: true, value: null };
    const num = Number(raw.replace(',', '.'));
    if (isNaN(num) || num < 0) return { valid: false, value: null };
    return { valid: true, value: num };
  };

  const handleSave = async () => {
    if (!editTarget) return;

    const { valid, value: priceNum } = validatePrice(editForm.price);
    if (!valid) {
      showToast({ message: t('admin.packages.invalidPrice', { defaultValue: 'Price must be a non-negative number.' }), type: 'error' });
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('authToken');
      const sortOrderNum = editForm.sortOrder.trim() !== '' ? Number(editForm.sortOrder) : 0;
      const grantsPayload = editForm.grants
        .filter(g => g.packageType && g.quantity.trim() !== '')
        .map(g => ({ packageType: g.packageType, quantity: Number(g.quantity) }));
      const response = await fetch(getApiUrl(`api/admin/available-packages/${editTarget.key}`), {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: editForm.label.trim(),
          isActive: editForm.isActive,
          price: priceNum,
          currency: editForm.currency.trim() || null,
          sortOrder: sortOrderNum,
          ...(grantsPayload.length > 0 ? { grants: grantsPayload } : {}),
        }),
      });

      if (response.ok) {
        showToast({ message: t('admin.packages.saveSuccess', { defaultValue: 'Package updated successfully.' }), type: 'success' });
        closeEdit();
        fetchPackages();
      } else {
        const err = await response.json();
        showToast({ message: err?.message || t('admin.packages.saveFailed', { defaultValue: 'Failed to update package.' }), type: 'error' });
      }
    } catch (error) {
      console.error('Error saving package:', error);
      showToast({ message: t('admin.packages.saveFailed', { defaultValue: 'Failed to update package.' }), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Create ──────────────────────────────────────────────────────────────

  const openCreate = () => {
    setCreateForm(emptyCreateForm());
    setCreateOpen(true);
  };

  const closeCreate = () => setCreateOpen(false);

  const addGrantRow = () =>
    setCreateForm(prev => ({ ...prev, grants: [...prev.grants, { packageType: 'JobPost', quantity: '1' }] }));

  const removeGrantRow = (index: number) =>
    setCreateForm(prev => ({ ...prev, grants: prev.grants.filter((_, i) => i !== index) }));

  const updateGrantRow = (index: number, field: keyof GrantRow, value: string) =>
    setCreateForm(prev => {
      const grants = [...prev.grants];
      grants[index] = { ...grants[index], [field]: value };
      return { ...prev, grants };
    });

  const handleCreate = async () => {
    if (!createForm.key.trim() || !createForm.label.trim()) {
      showToast({ message: t('admin.packages.createKeyLabelRequired', { defaultValue: 'Key and label are required.' }), type: 'error' });
      return;
    }

    const { valid, value: priceNum } = validatePrice(createForm.price);
    if (!valid) {
      showToast({ message: t('admin.packages.invalidPrice', { defaultValue: 'Price must be a non-negative number.' }), type: 'error' });
      return;
    }

    const grantsPayload = createForm.grants
      .filter(g => g.packageType && g.quantity.trim() !== '')
      .map(g => ({ packageType: g.packageType, quantity: Number(g.quantity) }));

    const sortOrderNum = createForm.sortOrder.trim() !== '' ? Number(createForm.sortOrder) : undefined;

    try {
      setCreating(true);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('api/admin/available-packages'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: createForm.key.trim(),
          label: createForm.label.trim(),
          price: priceNum,
          currency: createForm.currency.trim() || null,
          isActive: createForm.isActive,
          ...(sortOrderNum !== undefined ? { sortOrder: sortOrderNum } : {}),
          ...(grantsPayload.length > 0 ? { grants: grantsPayload } : {}),
        }),
      });

      if (response.ok) {
        showToast({ message: t('admin.packages.createSuccess', { defaultValue: 'Package created successfully.' }), type: 'success' });
        closeCreate();
        fetchPackages();
      } else {
        const err = await response.json();
        showToast({ message: err?.message || t('admin.packages.createFailed', { defaultValue: 'Failed to create package.' }), type: 'error' });
      }
    } catch (error) {
      console.error('Error creating package:', error);
      showToast({ message: t('admin.packages.createFailed', { defaultValue: 'Failed to create package.' }), type: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────

  const handleDelete = async (pkg: AvailablePackage) => {
    const confirmed = await confirm({
      title: t('admin.packages.deleteConfirmTitle', { defaultValue: 'Delete Package' }),
      message: t('admin.packages.deleteConfirmMsg', {
        defaultValue: `Are you sure you want to delete "{{label}}" ({{key}})? This cannot be undone.`,
        label: pkg.label,
        key: pkg.key,
      }),
      confirmLabel: t('admin.packages.deleteConfirm', { defaultValue: 'Delete' }),
      tone: 'danger',
    });

    if (!confirmed) return;

    try {
      setDeletingKey(pkg.key);
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl(`api/admin/available-packages/${pkg.key}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        showToast({ message: t('admin.packages.deleteSuccess', { defaultValue: 'Package deleted successfully.' }), type: 'success' });
        fetchPackages();
      } else {
        const err = await response.json();
        showToast({ message: err?.message || t('admin.packages.deleteFailed', { defaultValue: 'Failed to delete package.' }), type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting package:', error);
      showToast({ message: t('admin.packages.deleteFailed', { defaultValue: 'Failed to delete package.' }), type: 'error' });
    } finally {
      setDeletingKey(null);
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const formatPrice = (pkg: AvailablePackage) => {
    if (pkg.price === null || pkg.price === undefined) return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: pkg.currency ? 'currency' : 'decimal',
        ...(pkg.currency ? { currency: pkg.currency } : {}),
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(pkg.price);
    } catch {
      return `${pkg.price}${pkg.currency ? ' ' + pkg.currency : ''}`;
    }
  };

  const formatGrants = (grants: PackageGrant[]) => {
    if (!grants || grants.length === 0) return '—';
    return grants.map(g => `${g.quantity ?? g.count ?? '?'}× ${g.packageType}`).join(', ');
  };

  return (
    <div className={styles.adminPackages}>
      <div className={styles.listPageHeader}>
        <button
          onClick={() => navigate('/admin')}
          className={headerStyles.backButton}
          aria-label={t('common.back')}
          title={t('common.back')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={styles.headerTitle}>
          {t('admin.packages.title', { defaultValue: 'Package Management' })}
        </h1>
        <div />
      </div>

      <div className={styles.pageContent}>
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2>{t('admin.packages.availablePackages', { defaultValue: 'Available Packages' })}</h2>
            <div className={styles.sectionHeaderRight}>
              <span className={styles.packageCount}>
                {packages.length} {packages.length === 1
                  ? t('admin.packages.package', { defaultValue: 'package' })
                  : t('admin.packages.packages', { defaultValue: 'packages' })}
              </span>
              <button className={styles.createButton} onClick={openCreate}>
                + {t('admin.packages.createPackage', { defaultValue: 'New Package' })}
              </button>
            </div>
          </div>

          {loading ? (
            <div className={styles.sectionLoading}>{t('common.loading')}</div>
          ) : packages.length === 0 ? (
            <div className={styles.noResults}>
              {t('admin.packages.noPackages', { defaultValue: 'No packages found.' })}
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.packagesTable}>
                <thead>
                  <tr>
                    <th>{t('admin.packages.colKey', { defaultValue: 'Key' })}</th>
                    <th>{t('admin.packages.colLabel', { defaultValue: 'Label' })}</th>
                    <th>{t('admin.packages.colGrants', { defaultValue: 'Grants' })}</th>
                    <th>{t('admin.packages.colPrice', { defaultValue: 'Price' })}</th>
                    <th>{t('admin.packages.colSortOrder', { defaultValue: 'Sort Order' })}</th>
                    <th>{t('admin.packages.colStatus', { defaultValue: 'Status' })}</th>
                    <th>{t('admin.packages.colActions', { defaultValue: 'Actions' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg) => (
                    <tr key={pkg.key}>
                      <td>
                        <code className={styles.keyBadge}>{pkg.key}</code>
                      </td>
                      <td>{pkg.label}</td>
                      <td className={styles.grantsCell}>{formatGrants(pkg.grants)}</td>
                      <td>{formatPrice(pkg)}</td>
                      <td>{pkg.sortOrder}</td>
                      <td>
                        <span className={pkg.isActive ? styles.statusActive : styles.statusInactive}>
                          {pkg.isActive
                            ? t('admin.packages.active', { defaultValue: 'Active' })
                            : t('admin.packages.inactive', { defaultValue: 'Inactive' })}
                        </span>
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button className={styles.editButton} onClick={() => openEdit(pkg)}>
                            {t('admin.packages.edit', { defaultValue: 'Edit' })}
                          </button>
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDelete(pkg)}
                            disabled={deletingKey === pkg.key}
                          >
                            {deletingKey === pkg.key
                              ? t('common.loading')
                              : t('admin.packages.delete', { defaultValue: 'Delete' })}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editTarget && (
        <div className={adminCommonStyles.modalOverlay} onClick={closeEdit}>
          <div className={adminCommonStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>
                {t('admin.packages.editTitle', { defaultValue: 'Edit Package' })}
                {' — '}
                <code>{editTarget.key}</code>
              </h2>
              <button className={adminCommonStyles.modalClose} onClick={closeEdit} aria-label={t('common.close')}>
                ✕
              </button>
            </div>

            <div className={adminCommonStyles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('admin.packages.fieldLabel', { defaultValue: 'Label' })}
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={editForm.label}
                  onChange={(e) => setEditForm(prev => ({ ...prev, label: e.target.value }))}
                  placeholder={t('admin.packages.fieldLabel', { defaultValue: 'Label' })}
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('admin.packages.fieldPrice', { defaultValue: 'Price' })}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={styles.formInput}
                    value={editForm.price}
                    onChange={(e) => setEditForm(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('admin.packages.fieldCurrency', { defaultValue: 'Currency' })}
                  </label>
                  <input
                    type="text"
                    maxLength={3}
                    className={styles.formInput}
                    value={editForm.currency}
                    onChange={(e) => setEditForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                    placeholder="RSD"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={editForm.isActive}
                    onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  {t('admin.packages.fieldActive', { defaultValue: 'Active (visible to users)' })}
                </label>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t('admin.packages.fieldSortOrder', { defaultValue: 'Sort Order' })}
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className={styles.formInput}
                  value={editForm.sortOrder}
                  onChange={(e) => setEditForm(prev => ({ ...prev, sortOrder: e.target.value }))}
                  placeholder="0"
                />
              </div>

              <div className={styles.grantsSection}>
                <div className={styles.grantsSectionHeader}>
                  <span className={styles.formLabel}>{t('admin.packages.fieldGrants', { defaultValue: 'Grants' })}</span>
                  <button className={styles.addGrantButton} type="button" onClick={() => setEditForm(prev => ({ ...prev, grants: [...prev.grants, { packageType: 'JobPost', quantity: '1' }] }))}>
                    + {t('admin.packages.addGrant', { defaultValue: 'Add Grant' })}
                  </button>
                </div>

                {editForm.grants.length === 0 ? (
                  <p className={styles.noGrantsHint}>{t('admin.packages.noGrantsHint', { defaultValue: 'No grants added yet.' })}</p>
                ) : (
                  <div className={styles.grantRows}>
                    {editForm.grants.map((grant, i) => (
                      <div key={i} className={styles.grantRow}>
                        <select
                          className={styles.grantSelect}
                          value={grant.packageType}
                          onChange={(e) => setEditForm(prev => {
                            const grants = [...prev.grants];
                            grants[i] = { ...grants[i], packageType: e.target.value };
                            return { ...prev, grants };
                          })}
                          aria-label={t('admin.packages.grantType', { defaultValue: 'Grant type' })}
                        >
                          <option value="JobPost">JobPost</option>
                          <option value="QuickSearch">QuickSearch</option>
                        </select>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className={`${styles.formInput} ${styles.grantQtyInput}`}
                          value={grant.quantity}
                          onChange={(e) => setEditForm(prev => {
                            const grants = [...prev.grants];
                            grants[i] = { ...grants[i], quantity: e.target.value };
                            return { ...prev, grants };
                          })}
                          placeholder="1"
                        />
                        <button
                          className={styles.removeGrantButton}
                          type="button"
                          onClick={() => setEditForm(prev => ({ ...prev, grants: prev.grants.filter((_, idx) => idx !== i) }))}
                          aria-label={t('common.remove', { defaultValue: 'Remove' })}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={adminCommonStyles.modalFooter}>
              <button className={adminCommonStyles.btnCancel} onClick={closeEdit}>{t('common.cancel')}</button>
              <button
                className={styles.saveButton}
                onClick={handleSave}
                disabled={saving || !editForm.label.trim()}
              >
                {saving ? t('common.loading') : t('common.save', { defaultValue: 'Save' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Modal ── */}
      {createOpen && (
        <div className={adminCommonStyles.modalOverlay} onClick={closeCreate}>
          <div className={`${adminCommonStyles.modalContent} ${styles.createModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>{t('admin.packages.createTitle', { defaultValue: 'New Package' })}</h2>
              <button className={adminCommonStyles.modalClose} onClick={closeCreate} aria-label={t('common.close')}>✕</button>
            </div>

            <div className={adminCommonStyles.modalBody}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('admin.packages.fieldKey', { defaultValue: 'Key' })}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={createForm.key}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, key: e.target.value }))}
                    placeholder="e.g. premium"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('admin.packages.fieldLabel', { defaultValue: 'Label' })}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={createForm.label}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g. Premium Package"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('admin.packages.fieldPrice', { defaultValue: 'Price' })}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={styles.formInput}
                    value={createForm.price}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('admin.packages.fieldCurrency', { defaultValue: 'Currency' })}</label>
                  <input
                    type="text"
                    maxLength={3}
                    className={styles.formInput}
                    value={createForm.currency}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                    placeholder="RSD"
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{t('admin.packages.fieldSortOrder', { defaultValue: 'Sort Order' })}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className={styles.formInput}
                    value={createForm.sortOrder}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, sortOrder: e.target.value }))}
                    placeholder="0"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={`${styles.toggleLabel} ${styles.toggleLabelSpaced}`}>
                    <input
                      type="checkbox"
                      className={styles.toggleInput}
                      checked={createForm.isActive}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    />
                    {t('admin.packages.fieldActive', { defaultValue: 'Active (visible to users)' })}
                  </label>
                </div>
              </div>

              <div className={styles.grantsSection}>
                <div className={styles.grantsSectionHeader}>
                  <span className={styles.formLabel}>{t('admin.packages.fieldGrants', { defaultValue: 'Grants' })}</span>
                  <button className={styles.addGrantButton} type="button" onClick={addGrantRow}>
                    + {t('admin.packages.addGrant', { defaultValue: 'Add Grant' })}
                  </button>
                </div>

                {createForm.grants.length === 0 ? (
                  <p className={styles.noGrantsHint}>{t('admin.packages.noGrantsHint', { defaultValue: 'No grants added yet.' })}</p>
                ) : (
                  <div className={styles.grantRows}>
                    {createForm.grants.map((grant, i) => (
                      <div key={i} className={styles.grantRow}>
                        <select
                          className={styles.grantSelect}
                          value={grant.packageType}
                          onChange={(e) => updateGrantRow(i, 'packageType', e.target.value)}
                          aria-label={t('admin.packages.grantType', { defaultValue: 'Grant type' })}
                        >
                          <option value="JobPost">JobPost</option>
                          <option value="QuickSearch">QuickSearch</option>
                        </select>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          className={`${styles.formInput} ${styles.grantQtyInput}`}
                          value={grant.quantity}
                          onChange={(e) => updateGrantRow(i, 'quantity', e.target.value)}
                          placeholder="1"
                        />
                        <button
                          className={styles.removeGrantButton}
                          type="button"
                          onClick={() => removeGrantRow(i)}
                          aria-label={t('common.remove', { defaultValue: 'Remove' })}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={adminCommonStyles.modalFooter}>
              <button className={adminCommonStyles.btnCancel} onClick={closeCreate}>{t('common.cancel')}</button>
              <button
                className={styles.saveButton}
                onClick={handleCreate}
                disabled={creating || !createForm.key.trim() || !createForm.label.trim()}
              >
                {creating ? t('common.loading') : t('admin.packages.createPackage', { defaultValue: 'Create Package' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
